import React, { forwardRef, useCallback, useState } from 'react'

import FormattedInput, { BaseFormattedInputProps } from '../Input/FormattedInput'

export type DateInputFormat = 'month-first' | 'day-first'
export type DateOutputFormat = 'month-first' | 'iso' | 'year-first'

export interface BirthdateInputProps extends Omit<BaseFormattedInputProps, 'onChange' | 'value'> {
  value?: string

  onChange: (value: string) => void

  inputFormat?: DateInputFormat

  outputFormat?: DateOutputFormat

  placeholder?: string
}

const BIRTHDATE_TEMPLATE = '##/##/####'

const BirthdateInput = forwardRef<HTMLInputElement, BirthdateInputProps>(
  (
    { value = '', onChange, inputFormat = 'month-first', outputFormat = 'month-first', placeholder, ...restProps },
    ref
  ) => {
    const [internalDisplayValue, setInternalDisplayValue] = useState(value)

    const formatOutputValue = useCallback(
      (displayValue: string): string => {
        if (!displayValue) return ''

        let mmddyyyy = displayValue
        if (inputFormat === 'day-first' && displayValue.length === 8) {
          const day = displayValue.slice(0, 2)
          const month = displayValue.slice(2, 4)
          const year = displayValue.slice(4, 8)
          mmddyyyy = `${month}${day}${year}`
        }

        if (outputFormat === 'month-first') {
          if (mmddyyyy.length === 8) {
            return `${mmddyyyy.slice(0, 2)}/${mmddyyyy.slice(2, 4)}/${mmddyyyy.slice(4)}`
          }
          return mmddyyyy
        } else if (outputFormat === 'iso') {
          if (mmddyyyy.length === 8) {
            return `${mmddyyyy.slice(4)}-${mmddyyyy.slice(0, 2)}-${mmddyyyy.slice(2, 4)}`
          }
          return mmddyyyy
        } else if (outputFormat === 'year-first') {
          if (mmddyyyy.length === 8) {
            return `${mmddyyyy.slice(4)}/${mmddyyyy.slice(0, 2)}/${mmddyyyy.slice(2, 4)}`
          }
          return mmddyyyy
        }

        return mmddyyyy
      },
      [inputFormat, outputFormat]
    )

    const handleChange = useCallback(
      (newDisplayValue: string) => {
        setInternalDisplayValue(newDisplayValue)
        const outputValue = formatOutputValue(newDisplayValue)
        onChange(outputValue)
      },
      [formatOutputValue, onChange]
    )

    return (
      <FormattedInput
        {...restProps}
        ref={ref}
        value={internalDisplayValue}
        onChange={handleChange}
        template={BIRTHDATE_TEMPLATE}
        placeholder={placeholder}
        inputMode="numeric"
        showTemplateInInput={true}
      />
    )
  }
)

BirthdateInput.displayName = 'BirthdateInput'

export default BirthdateInput
